import { useState, useRef } from "react";
import {
  useAdminMessages,
  useAdminCreateMessage,
  useAdminUpdateMessage,
  useAdminDeleteMessage,
  useAdminMe,
} from "../hooks/useApi";
import toast from "react-hot-toast";
import "../admin-theme.css";

const KIND_LABELS: Record<string, string> = {
  hint: "نکته",
  info: "اطلاع",
  announcement: "اعلامیه",
  warning: "هشدار",
  success: "موفقیت",
};

const KIND_OPTIONS = ["hint", "info", "announcement", "warning", "success"];

const KIND_BADGE: Record<string, { cls: string; bg?: string; color?: string }> = {
  hint: { cls: "admin-badge-warning" },
  info: { cls: "admin-badge-info" },
  announcement: { cls: "", bg: "var(--admin-primary-light)", color: "var(--admin-primary-dark)" },
  warning: { cls: "admin-badge-error" },
  success: { cls: "admin-badge-success" },
};

const PLACEMENT_LABELS: Record<string, string> = {
  all: "همه صفحات",
  dashboard: "داشبورد",
  models: "مدل‌ها",
  usage: "مصرف API",
  logs: "لاگ‌ها",
  wallet: "کیف پول",
  profile: "پروفایل",
  subscription: "اشتراک",
  "api-keys": "کلیدهای API",
  support: "پشتیبانی",
  docs: "مستندات",
  partners: "داشبورد همکاران",
};

const PLACEMENT_OPTIONS = Object.keys(PLACEMENT_LABELS);

interface MessageForm {
  announcement_id: number | null;
  kind: string;
  title: string;
  body: string;
  placements: string;
  start: string;
  end: string;
  is_active: boolean;
}

const EMPTY_FORM: MessageForm = {
  announcement_id: null,
  kind: "announcement",
  title: "",
  body: "",
  placements: "all",
  start: "",
  end: "",
  is_active: true,
};

const toInputValue = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromInputValue = (value: string): string | null =>
  value ? new Date(value).toISOString() : null;

const splitPlacements = (raw: string): string[] =>
  raw
    ? raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : ["all"];

const isAllPlacements = (raw: string): boolean => splitPlacements(raw).includes("all");

export default function AdminMessages() {
  const { data: messages, isLoading } = useAdminMessages();
  const { data: me } = useAdminMe();
  const createMessage = useAdminCreateMessage();
  const updateMessage = useAdminUpdateMessage();
  const deleteMessage = useAdminDeleteMessage();

  const canManage = Boolean(me?.is_admin) || Boolean(me?.permissions?.includes("messages.manage"));
  const [form, setForm] = useState<MessageForm | null>(null);
  const overlayMouseDown = useRef(false);

  const isVisibleNow = (m: Record<string, unknown>): boolean => {
    if (!m.is_active) return false;
    const now = Date.now();
    if (m.start_date && new Date(m.start_date as string).getTime() > now) return false;
    if (m.end_date && new Date(m.end_date as string).getTime() <= now) return false;
    return true;
  };

  const kindBadge = (kind: string) => {
    const cfg = KIND_BADGE[kind] || KIND_BADGE.info;
    return (
      <span
        className={`admin-badge ${cfg.cls}`}
        style={{ background: cfg.bg, color: cfg.color }}
      >
        {KIND_LABELS[kind] || kind}
      </span>
    );
  };

  const openCreate = () => setForm({ ...EMPTY_FORM });

  const openEdit = (m: Record<string, unknown>) => {
    setForm({
      announcement_id: m.announcement_id as number,
      kind: (m.kind as string) || "announcement",
      title: (m.title as string) || "",
      body: (m.body as string) || "",
      placements: (m.placements as string) || "all",
      start: toInputValue(m.start_date as string),
      end: toInputValue(m.end_date as string),
      is_active: Boolean(m.is_active),
    });
  };

  const togglePlacement = (p: string) => {
    setForm((f) => {
      if (!f) return f;
      let list = splitPlacements(f.placements);
      if (p === "all") {
        list = ["all"];
      } else if (list.includes("all")) {
        list = [p];
      } else if (list.includes(p)) {
        list = list.filter((x) => x !== p);
      } else {
        list = [...list, p];
      }
      if (list.length === 0) list = ["all"];
      return { ...f, placements: list.join(",") };
    });
  };

  const placementBadge = (raw: string) => {
    const list = splitPlacements(raw);
    return (
      <div className="flex flex-wrap items-center gap-1">
        {list.map((p) => (
          <span
            key={p}
            className={`admin-badge ${p === "all" ? "admin-badge-info" : ""}`}
          >
            {PLACEMENT_LABELS[p] || p}
          </span>
        ))}
      </div>
    );
  };

  const handleSave = () => {
    if (!form) return;
    if (!form.title.trim()) {
      toast.error("عنوان اطلاع‌رسانی الزامی است");
      return;
    }
    if (!form.body.trim()) {
      toast.error("متن اطلاع‌رسانی الزامی است");
      return;
    }
    if (form.start && form.end && new Date(form.end) <= new Date(form.start)) {
      toast.error("تاریخ پایان باید بعد از تاریخ شروع باشد");
      return;
    }

    const payload: Record<string, unknown> = {
      kind: form.kind,
      title: form.title.trim(),
      body: form.body.trim(),
      placements: form.placements,
      start_date: fromInputValue(form.start),
      end_date: fromInputValue(form.end),
      is_active: form.is_active,
    };

    if (form.announcement_id != null) {
      updateMessage.mutate({ id: form.announcement_id, data: payload });
    } else {
      createMessage.mutate(payload);
    }
    setForm(null);
  };

  const handleDelete = (m: Record<string, unknown>) => {
    if (!window.confirm(`«${m.title}» حذف شود؟`)) return;
    deleteMessage.mutate(m.announcement_id as number);
  };

  const fmtDate = (iso?: string | null): string =>
    iso ? new Date(iso).toLocaleDateString("fa-IR") : "—";

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>اطلاع‌رسانی‌ها</h1>
          <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>
            پیام‌های سیستمی که در بالای صفحات داشبورد کاربران نمایش داده می‌شوند
          </p>
        </div>
        <button
          className="admin-btn admin-btn-primary"
          disabled={!canManage}
          style={{ opacity: canManage ? 1 : 0.4, cursor: canManage ? "pointer" : "not-allowed" }}
          onClick={openCreate}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="ml-1.5">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          اطلاع‌رسانی جدید
        </button>
      </div>

      {/* Messages Table */}
      <div className="admin-card overflow-hidden">
        <div className="admin-card-header">
          <h2 className="admin-card-title">لیست اطلاع‌رسانی‌ها</h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : !messages?.length ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3 opacity-50" stroke="currentColor" strokeWidth="1.5">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <p className="text-sm">هیچ اطلاع‌رسانی‌ای ثبت نشده است</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>نوع</th>
                  <th>عنوان</th>
                  <th>نمایش در</th>
                  <th>وضعیت</th>
                  <th>تاریخ شروع</th>
                  <th>تاریخ پایان</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {(messages as Record<string, unknown>[]).map((m) => {
                  const visible = isVisibleNow(m);
                  return (
                    <tr key={m.announcement_id as number}>
                      <td>{kindBadge(m.kind as string)}</td>
                      <td>
                        <div>
                          <span className="text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>
                            {m.title as string}
                          </span>
                          <p className="text-xs mt-0.5 max-w-xs truncate" style={{ color: "var(--admin-text-tertiary)" }}>
                            {m.body as string}
                          </p>
                        </div>
                      </td>
                      <td>{placementBadge((m.placements as string) || "all")}</td>
                      <td>
                        {m.is_active ? (
                          <span className="admin-badge admin-badge-success">فعال</span>
                        ) : (
                          <span className="admin-badge admin-badge-warning">غیرفعال</span>
                        )}
                        {visible && <span className="admin-badge admin-badge-info mr-1">در حال نمایش</span>}
                      </td>
                      <td>
                        <span className="text-xs" style={{ color: "var(--admin-text-secondary)" }}>
                          {fmtDate(m.start_date as string)}
                        </span>
                      </td>
                      <td>
                        <span className="text-xs" style={{ color: "var(--admin-text-secondary)" }}>
                          {fmtDate(m.end_date as string)}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            className="admin-btn admin-btn-secondary admin-btn-sm"
                            disabled={!canManage}
                            style={{ opacity: canManage ? 1 : 0.4, cursor: canManage ? "pointer" : "not-allowed" }}
                            onClick={() => openEdit(m)}
                          >
                            ویرایش
                          </button>
                          <button
                            className="admin-btn admin-btn-sm"
                            disabled={!canManage}
                            style={{
                              background: "var(--admin-error-bg)",
                              color: "var(--admin-error)",
                              border: "none",
                              opacity: canManage ? 1 : 0.4,
                              cursor: canManage ? "pointer" : "not-allowed",
                            }}
                            onClick={() => handleDelete(m)}
                          >
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {form && (
        <div
          className="admin-overlay"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setForm(null); overlayMouseDown.current = false; }}
        >
          <div
            className="admin-glass admin-modal"
            style={{ maxWidth: 560, maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                {form.announcement_id != null ? "ویرایش اطلاع‌رسانی" : "اطلاع‌رسانی جدید"}
              </h2>
              <button
                onClick={() => setForm(null)}
                className="p-1 rounded-lg transition-colors"
                style={{ color: "var(--admin-text-tertiary)" }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Kind */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  نوع
                </label>
                <select
                  value={form.kind}
                  onChange={(e) => setForm((f) => f && { ...f, kind: e.target.value })}
                  className="admin-input"
                >
                  {KIND_OPTIONS.map((k) => (
                    <option key={k} value={k}>{KIND_LABELS[k]}</option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  عنوان
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => f && { ...f, title: e.target.value })}
                  className="admin-input"
                  placeholder="مثلاً به‌روزرسانی سرویس"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  متن
                </label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm((f) => f && { ...f, body: e.target.value })}
                  className="admin-input"
                  style={{ minHeight: 96, resize: "vertical" }}
                  placeholder="متن پیام برای کاربران"
                />
              </div>

              {/* Placements */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  نمایش در (کدام تب؟)
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {PLACEMENT_OPTIONS.map((p) => {
                    const active = isAllPlacements(form.placements)
                      ? p === "all"
                      : splitPlacements(form.placements).includes(p);
                    return (
                      <button
                        type="button"
                        key={p}
                        onClick={() => togglePlacement(p)}
                        className="admin-badge"
                        style={{
                          cursor: "pointer",
                          background: active ? "var(--admin-primary)" : "var(--admin-bg-hover)",
                          color: active ? "#fff" : "var(--admin-text-secondary)",
                          border: active ? "1px solid var(--admin-primary)" : "1px solid var(--admin-border)",
                        }}
                      >
                        {PLACEMENT_LABELS[p]}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs mt-1.5" style={{ color: "var(--admin-text-tertiary)" }}>
                  «همه صفحات» یعنی پیام در تمام تب‌ها نمایش داده می‌شود؛ در غیر این صورت فقط در تب‌های انتخابی.
                </p>
              </div>

              {/* Schedule */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    تاریخ شروع (اختیاری)
                  </label>
                  <input
                    type="datetime-local"
                    value={form.start}
                    onChange={(e) => setForm((f) => f && { ...f, start: e.target.value })}
                    className="admin-input"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    تاریخ پایان (اختیاری)
                  </label>
                  <input
                    type="datetime-local"
                    value={form.end}
                    onChange={(e) => setForm((f) => f && { ...f, end: e.target.value })}
                    className="admin-input"
                    dir="ltr"
                  />
                </div>
              </div>
              <p className="text-xs -mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
                بدون تاریخ = همیشه نمایش داده می‌شود
              </p>

              {/* Active */}
              <label className="flex items-center justify-between rounded-xl px-4 py-3 cursor-pointer" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                <span className="text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>فعال</span>
                <button
                  type="button"
                  className={`admin-toggle${form.is_active ? " on" : ""}`}
                  onClick={() => setForm((f) => f && { ...f, is_active: !f.is_active })}
                />
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setForm(null)}
                  className="admin-btn admin-btn-secondary flex-1"
                >
                  انصراف
                </button>
                <button
                  onClick={handleSave}
                  disabled={createMessage.isPending || updateMessage.isPending}
                  className="admin-btn admin-btn-primary flex-1"
                >
                  {createMessage.isPending || updateMessage.isPending ? "در حال ذخیره..." : "ذخیره"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}